import { c as _c } from "react/compiler-runtime";
import Fade from '@mui/material/Fade';
import type { Modifier } from '@popperjs/core';
import React, { forwardRef, useRef } from 'react';
import { DEFAULT_RICHTEXT_TOOLTIP_WIDTH, RichBannerContainer, RichBannerHeader, RichCaptionContainer, RichHeaderContainer, RichHeaderTextContent, RichMetricRowContainer, RichMetricTitleWrapper, RichMetricValueWrapper, StyledTooltip, TooltipBodyContainer, TooltipContainer, TooltipTitleContainer, TooltipTypography } from './styles';
import { type CustomFlipModifier, type CustomOffsetModifier, type MultiLineTooltipProps, type TooltipBodyProps, type TooltipProps, type TooltipRichBannerProps, type TooltipRichCaptionProps, type TooltipRichHeaderProps, type TooltipRichMetricRowProps, type TooltipTitleProps } from './types';
const flipModifier: CustomFlipModifier = {
  name: 'flip',
  enabled: true,
  phase: 'main',
  options: {
    fallbackPlacements: ['top-end', 'top-start', 'bottom-start', 'top', 'bottom', 'right', 'left']
  }
};
const DELAY_IN_MS = 500;
const DEFAULT_VERTICAL_OFFSET = -9;
const offsetModifier: CustomOffsetModifier = {
  name: 'offset',
  enabled: true,
  phase: 'main',
  options: {
    /**
     * Function that calculates the offset to apply to the tooltip depending on
     * the placement that Popper calculates for it.
     */
    offset: ({
      placement,
      reference
    }) => {
      switch (placement) {
        case 'bottom-start':
          return [reference.width / 2, DEFAULT_VERTICAL_OFFSET];
        case 'bottom-end':
          return [-(reference.width / 2) - 2, DEFAULT_VERTICAL_OFFSET];
        case 'top-start':
          return [reference.width / 2 - 2, DEFAULT_VERTICAL_OFFSET];
        case 'top-end':
          return [-(reference.width / 2) - 2, DEFAULT_VERTICAL_OFFSET];
        case 'top':
        case 'bottom':
          return [0, DEFAULT_VERTICAL_OFFSET];
        default:
          return [0, 0];
      }
    }
  }
};
const setBorderRadiusModifier: Modifier<'setBorderRadius', Record<string, never>> = {
  name: 'setBorderRadius',
  enabled: true,
  phase: 'afterWrite',
  fn({
    state
  }) {
    /**
     * Function used to find the actual MUI Tooltip and change the border radius
     * dependent on the placement to generate the "arrow" effect.
     */
    const MuiTooltipRef: HTMLElement | null = state.elements.popper.querySelector('.MuiTooltip-tooltip');
    if (MuiTooltipRef != null) {
      if (state.placement === 'bottom-start') {
        MuiTooltipRef.style.borderTopLeftRadius = '0px';
      } else if (state.placement === 'bottom-end') {
        MuiTooltipRef.style.borderTopRightRadius = '0px';
      } else if (state.placement === 'top-start') {
        MuiTooltipRef.style.borderBottomLeftRadius = '0px';
      } else if (state.placement === 'top-end') {
        MuiTooltipRef.style.borderBottomRightRadius = '0px';
      }
    }
  }
};
export const TooltipTitle = t0 => {
  const $ = _c(3);
  const {
    text,
    adornment
  } = t0;
  const t1 = adornment != null && adornment;
  let t2;
  if ($[0] !== t1 || $[1] !== text) {
    t2 = <TooltipTitleContainer>{text}{t1}</TooltipTitleContainer>;
    $[0] = t1;
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const TooltipBody = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipBodyContainer>{children}</TooltipBodyContainer>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const TooltipRichHeader = t0 => {
  const $ = _c(10);
  const {
    avatar,
    title,
    subtitle
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <TooltipTypography variant="body2">{title}</TooltipTypography>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== subtitle) {
    t2 = subtitle != null && subtitle !== "" && <TooltipTypography variant="body3">{subtitle}</TooltipTypography>;
    $[2] = subtitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <RichHeaderTextContent>{t1}{t2}</RichHeaderTextContent>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== avatar || $[8] !== t3) {
    t4 = <RichHeaderContainer>{avatar}{t3}</RichHeaderContainer>;
    $[7] = avatar;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const TooltipRichCaption = t0 => {
  const $ = _c(5);
  const {
    text,
    statusIndicator
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <TooltipTypography variant="body3">{text}</TooltipTypography>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== statusIndicator || $[3] !== t1) {
    t2 = <RichCaptionContainer>{t1}{statusIndicator}</RichCaptionContainer>;
    $[2] = statusIndicator;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const TooltipRichMetricRow = t0 => {
  const $ = _c(15);
  const {
    statusIndicator,
    title,
    value,
    unit
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <TooltipTypography variant="body2">{title}</TooltipTypography>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== statusIndicator || $[3] !== t1) {
    t2 = <RichMetricTitleWrapper>{statusIndicator}{t1}</RichMetricTitleWrapper>;
    $[2] = statusIndicator;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== value) {
    t3 = <TooltipTypography variant="h5">{value}</TooltipTypography>;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== unit) {
    t4 = unit != null && unit !== "" && <TooltipTypography variant="h5">{unit}</TooltipTypography>;
    $[7] = unit;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <RichMetricValueWrapper>{t3}{t4}</RichMetricValueWrapper>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <RichMetricRowContainer>{t2}{t5}</RichMetricRowContainer>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export const TooltipRichBanner = t0 => {
  const $ = _c(10);
  const {
    statusIndicator,
    title,
    description
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <TooltipTypography variant="h5">{title}</TooltipTypography>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== statusIndicator || $[3] !== t1) {
    t2 = <RichBannerHeader>{t1}{statusIndicator}</RichBannerHeader>;
    $[2] = statusIndicator;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== description) {
    t3 = <TooltipTypography variant="body2">{description}</TooltipTypography>;
    $[5] = description;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <RichBannerContainer>{t2}{t3}</RichBannerContainer>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const Tooltip = forwardRef<HTMLDivElement, TooltipProps>((t0, ref) => {
  const $ = _c(64);
  let children;
  let kind;
  let maxHeight;
  let maxWidth;
  let onClose;
  let onContentClick;
  let onOpen;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let title;
  let titleAdornment;
  let titleBodyGap;
  let width;
  if ($[0] !== t0) {
    ({
      open: t1,
      kind,
      title,
      children,
      placement: t2,
      width,
      maxHeight,
      allowOverflow: t3,
      maxWidth,
      disabled: t4,
      titleBodyGap,
      shouldDelay: t5,
      onOpen,
      onClose,
      interactive: t6,
      onContentClick,
      titleAdornment,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = kind;
    $[3] = maxHeight;
    $[4] = maxWidth;
    $[5] = onClose;
    $[6] = onContentClick;
    $[7] = onOpen;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = title;
    $[16] = titleAdornment;
    $[17] = titleBodyGap;
    $[18] = width;
  } else {
    children = $[1];
    kind = $[2];
    maxHeight = $[3];
    maxWidth = $[4];
    onClose = $[5];
    onContentClick = $[6];
    onOpen = $[7];
    props = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    title = $[15];
    titleAdornment = $[16];
    titleBodyGap = $[17];
    width = $[18];
  }
  const open = t1 === undefined ? false : t1;
  const placement = t2 === undefined ? "bottom-end" : t2;
  const allowOverflow = t3 === undefined ? false : t3;
  const disabled = t4 === undefined ? false : t4;
  const shouldDelay = t5 === undefined ? false : t5;
  const interactive = t6 === undefined ? false : t6;
  const [tooltipIsOpen, setTooltipIsOpen] = React.useState(open);
  const initialOpenState = useRef(tooltipIsOpen);
  let t7;
  if ($[19] !== interactive || $[20] !== kind || $[21] !== onContentClick || $[22] !== props || $[23] !== title || $[24] !== titleAdornment || $[25] !== titleBodyGap) {
    t7 = () => {
      const onClick = event => {
        event.stopPropagation();
        if (interactive && onContentClick != null) {
          onContentClick();
          setTooltipIsOpen(false);
        }
      };
      const renderContent = () => {
        if (kind === "singleline" && title !== null) {
          return <TooltipContainer onClick={onClick} kind={kind} titleBodyGap={titleBodyGap}><TooltipTitle text={title} adornment={titleAdornment} /></TooltipContainer>;
        }
        if (kind === "multiline") {
          const multilineProps = props as MultiLineTooltipProps;
          if (multilineProps.content !== undefined) {
            return <TooltipContainer onClick={onClick} kind={kind} titleBodyGap={titleBodyGap}>{multilineProps.content}</TooltipContainer>;
          }
          if (multilineProps.body !== undefined) {
            return <TooltipContainer onClick={onClick} kind={kind} titleBodyGap={titleBodyGap}><TooltipTitle text={title} adornment={titleAdornment} /><TooltipBody>{multilineProps.body}</TooltipBody></TooltipContainer>;
          }
        }
        return <></>;
      };
      return <>{renderContent()}</>;
    };
    $[19] = interactive;
    $[20] = kind;
    $[21] = onContentClick;
    $[22] = props;
    $[23] = title;
    $[24] = titleAdornment;
    $[25] = titleBodyGap;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const TooltipContent = t7;
  let t8;
  if ($[27] !== onOpen) {
    t8 = () => {
      if (!initialOpenState.current) {
        setTooltipIsOpen(true);
      }
      onOpen?.();
    };
    $[27] = onOpen;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleOpen = t8;
  let t9;
  if ($[29] !== onClose) {
    t9 = () => {
      if (!initialOpenState.current) {
        setTooltipIsOpen(false);
      }
      onClose?.();
    };
    $[29] = onClose;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const handleClose = t9;
  let modifiers;
  bb0: switch (placement) {
    case "right":
      {
        let t10;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = [offsetModifier, offsetModifier];
          $[31] = t10;
        } else {
          t10 = $[31];
        }
        modifiers = t10;
        break bb0;
      }
    default:
      {
        let t10;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = [flipModifier, offsetModifier, setBorderRadiusModifier];
          $[32] = t10;
        } else {
          t10 = $[32];
        }
        modifiers = t10;
      }
  }
  const t10 = props as MultiLineTooltipProps;
  let tooltipPropsToSpread;
  if ($[33] !== t10) {
    const {
      body,
      content,
      kind: _omitKind,
      ...t11
    } = t10;
    tooltipPropsToSpread = t11;
    $[33] = t10;
    $[34] = tooltipPropsToSpread;
  } else {
    tooltipPropsToSpread = $[34];
  }
  const effectiveWidth = width ?? (kind === "multiline" && (props as MultiLineTooltipProps).content != null ? DEFAULT_RICHTEXT_TOOLTIP_WIDTH : undefined);
  let t11;
  if ($[35] !== TooltipContent) {
    t11 = <TooltipContent />;
    $[35] = TooltipContent;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const t12 = shouldDelay ? DELAY_IN_MS : undefined;
  const t13 = shouldDelay ? DELAY_IN_MS : undefined;
  let t14;
  if ($[37] !== modifiers) {
    t14 = {
      modifiers
    };
    $[37] = modifiers;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const t15 = interactive ? "auto" : "none";
  let t16;
  if ($[39] !== t15) {
    t16 = {
      pointerEvents: t15
    };
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t14 || $[42] !== t16) {
    t17 = {
      popperOptions: t14,
      style: t16
    };
    $[41] = t14;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== disabled) {
    t18 = disabled && {
      disableFocusListener: true,
      disableHoverListener: true
    };
    $[44] = disabled;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = children as React.ReactElement;
  let t20;
  if ($[46] !== allowOverflow || $[47] !== effectiveWidth || $[48] !== handleClose || $[49] !== handleOpen || $[50] !== kind || $[51] !== maxHeight || $[52] !== maxWidth || $[53] !== placement || $[54] !== ref || $[55] !== t11 || $[56] !== t12 || $[57] !== t13 || $[58] !== t17 || $[59] !== t18 || $[60] !== t19 || $[61] !== tooltipIsOpen || $[62] !== tooltipPropsToSpread) {
    t20 = <StyledTooltip title={t11} kind={kind} enterDelay={t12} enterNextDelay={t13} placement={placement} width={effectiveWidth} maxWidth={maxWidth} maxHeight={maxHeight} allowOverflow={allowOverflow} open={tooltipIsOpen} onOpen={handleOpen} onClose={handleClose} PopperProps={t17} TransitionComponent={Fade} ref={ref} {...t18} {...tooltipPropsToSpread}>{t19}</StyledTooltip>;
    $[46] = allowOverflow;
    $[47] = effectiveWidth;
    $[48] = handleClose;
    $[49] = handleOpen;
    $[50] = kind;
    $[51] = maxHeight;
    $[52] = maxWidth;
    $[53] = placement;
    $[54] = ref;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
    $[61] = tooltipIsOpen;
    $[62] = tooltipPropsToSpread;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  return t20;
});
export { DEFAULT_RICHTEXT_TOOLTIP_WIDTH, TooltipTitleContainer, TooltipTypography } from './styles';
export default Tooltip;
export { Tooltip };
export type { TooltipProps } from './types';